'use client';

import { useEffect } from 'react';
import Image from 'next/image';
import styles from './MedigapEnrollmentModal.module.scss';
import CloseButton from './eligibility-modal/CloseButton';

interface MedigapEnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  planName: string;
  phoneNumber: string;
  enrollmentCode?: string;
  partnerName?: string;
}

export default function MedigapEnrollmentModal({
  isOpen,
  onClose,
  planName,
  phoneNumber,
  enrollmentCode,
  partnerName,
}: MedigapEnrollmentModalProps) {
  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent scrolling on the body when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleCallToEnroll = () => {
    // Open phone dialer
    window.location.href = `tel:${phoneNumber.replace(/\D/g, '')}`;
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className={styles.modal}
      role="dialog"
      aria-modal="true"
      aria-labelledby="medigap-modal-title"
    >
      <button
        className={styles.backdrop}
        onClick={handleBackdropClick}
        onKeyDown={handleBackdropKeyDown}
        aria-label="Close modal"
        tabIndex={-1}
      />
      <div className={styles.modalContent}>
        <div className={styles.modalContainer}>
          {/* Top accent line */}
          <div className={styles.accentLine} />

          {/* Close button */}
          <CloseButton onClose={onClose} />

          {/* Content */}
          <div className={styles.content}>
            <div className={styles.contentSection}>
              <h2 id="medigap-modal-title" className={styles.title}>
                Enroll in this plan
              </h2>

              <div className={styles.description}>
                <p className={styles.planName}>{planName}</p>

                <p className={styles.callText}>
                  Call {partnerName ? `our partner, ${partnerName},` : 'us'} at{' '}
                  <span className={styles.phoneNumber}>{phoneNumber}</span>
                  {enrollmentCode && (
                    <>
                      {' '}
                      and share this code:{' '}
                      <span className={styles.enrollmentCode}>
                        {enrollmentCode}
                      </span>{' '}
                      with {partnerName ? 'their' : 'our'} agent
                    </>
                  )}{' '}
                  to enroll in this plan.
                </p>

                <p className={styles.disclaimer}>
                  Agents cannot give advice regarding Medicare or discuss plan
                  details.
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className={styles.buttonContainer}>
              <button className={styles.cancelButton} onClick={onClose}>
                <span className={styles.buttonText}>Cancel</span>
              </button>

              <button
                className={styles.enrollButton}
                onClick={handleCallToEnroll}
              >
                <span className={styles.buttonText}>Call to enroll</span>
                <Image
                  src="/Phone.svg"
                  alt="Phone icon"
                  className={styles.phoneIcon}
                  width={24}
                  height={24}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
